import { useState, useEffect, useCallback } from "react";
import { fetchAuthed, ApiError } from "@/utils/api";
import type { SubmissionView, GameItem } from "@/types/api";
import SubmissionCard from "./SubmissionCard";
import Spinner from "@/components/ui/Spinner";
import { CheckCircle, Layers } from "lucide-react";

interface ModerationQueueProps {
  gameId: string;
  myPlayerId: string;
  items: GameItem[];
  playerNames: Record<string, string>;
  onDecision?: () => void;
  refreshTrigger?: number;
}

export default function ModerationQueue({
  gameId,
  myPlayerId,
  items,
  playerNames,
  onDecision,
  refreshTrigger,
}: ModerationQueueProps) {
  const [subs, setSubs] = useState<SubmissionView[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const itemMap = Object.fromEntries(items.map((i) => [i.itemId, i.name]));

  const refresh = useCallback(async () => {
    try {
      const data = await fetchAuthed<{ submissions: SubmissionView[] }>(
        gameId,
        `/games/${gameId}/submissions`,
        { query: { status: "pending" } },
      );
      setSubs(data.submissions);
    } catch {
      // Swallow — will retry on next poll
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-refresh when parent signals new pending submissions
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      refresh();
    }
  }, [refreshTrigger, refresh]);

  const handleDecision = async (
    submissionId: string,
    decision: "approved" | "rejected",
  ) => {
    setActing(submissionId);
    setActionError(null);
    try {
      await fetchAuthed(
        gameId,
        `/games/${gameId}/submissions/${submissionId}`,
        { method: "PATCH", body: { decision } },
      );
      setSubs((prev) => prev.filter((s) => s.submissionId !== submissionId));
      onDecision?.();
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.message : "Failed to submit decision",
      );
    } finally {
      setActing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  // Filter out my own submissions (can't moderate own)
  const queue = subs.filter((s) => s.playerId !== myPlayerId);

  if (queue.length === 0) {
    return (
      <div className="flex flex-col items-center py-12 text-text-secondary">
        <CheckCircle className="mb-3 h-10 w-10 opacity-40" />
        <p className="text-base font-medium">All caught up!</p>
        <p className="mt-1 text-sm">No pending submissions to review</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Queue counter */}
      <div className="flex items-center justify-center gap-2 rounded-xl bg-surface-sunken px-4 py-2">
        <Layers className="h-4 w-4 text-text-secondary" />
        <span className="text-sm font-bold text-text-secondary">
          {queue.length} pending {queue.length === 1 ? "submission" : "submissions"}
        </span>
      </div>

      {actionError && (
        <div className="rounded-xl bg-error/10 px-4 py-3 text-sm font-medium text-error">
          {actionError}
        </div>
      )}

      {/* Show submissions one at a time, large */}
      <div className="mx-auto max-w-sm space-y-4">
        {queue.map((s, i) => (
          <div key={s.submissionId} className="animate-fade-in-up" style={{ animationDelay: `${i * 80}ms` }}>
          <SubmissionCard
            submission={s}
            playerName={playerNames[s.playerId] ?? "Unknown"}
            itemName={itemMap[s.itemId] ?? "Unknown Item"}
            showActions
            onApprove={() => handleDecision(s.submissionId, "approved")}
            onReject={() => handleDecision(s.submissionId, "rejected")}
            acting={acting === s.submissionId}
          />
          </div>
        ))}
      </div>
    </div>
  );
}
